import t from "@/lang/lang";
import { TextInputField } from "@/shared/components/field/input-field";
import { TextareaField } from "@/shared/components/field/textarea-field";
import { inputValidationErrorMessages } from "@/shared/components/errors/input-error-messages";
import { Address } from "@/services/backend/addresses/types/address";

/**
 * AddressFieldsDebounced collects an address for an auto-saving form.
 *
 * It differs from {@link AddressFields} in that its inputs debounce, so
 * the caller can render straight from the RTK Query cache and save through
 * useOptimisticDebouncedMutation without a local state buffer.
 *
 * AddressFields is the older local-state variant and is kept for the call
 * sites that have not been migrated yet.
 *
 * `error` is the error of the mutation; field messages are picked out of it
 * by the json field names the backend validates under.
 */
export function AddressFieldsDebounced({
  address,
  onAddressChange,
  error = undefined,
  fieldPrefix = "address",
  disabled = false,
}: {
  address: Address;
  onAddressChange: (address: Address) => void;
  error?: unknown;
  fieldPrefix?: string;
  disabled?: boolean;
}) {
  const errors = (field: string) =>
    inputValidationErrorMessages(
      error,
      fieldPrefix ? `${fieldPrefix}.${field}` : field,
    );

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <TextInputField
        label={t("Firma")}
        text={address.company}
        onTextChange={(company) => onAddressChange({ ...address, company })}
        errors={errors("company")}
        disabled={disabled}
      />
      <TextInputField
        label={t("Abteilung")}
        text={address.department}
        onTextChange={(department) =>
          onAddressChange({ ...address, department })
        }
        errors={errors("department")}
        disabled={disabled}
      />
      <TextInputField
        label={t("Ansprechpartner")}
        text={address.contact}
        onTextChange={(contact) => onAddressChange({ ...address, contact })}
        errors={errors("contact")}
        disabled={disabled}
      />
      <TextInputField
        label={t("Straße")}
        text={address.street}
        onTextChange={(street) => onAddressChange({ ...address, street })}
        errors={errors("street")}
        disabled={disabled}
      />
      <TextInputField
        label={t("Postfach")}
        text={address.postbox}
        onTextChange={(postbox) => onAddressChange({ ...address, postbox })}
        errors={errors("postbox")}
        disabled={disabled}
      />
      <div className="flex gap-4">
        <TextInputField
          label={t("PLZ")}
          fieldProps={{ className: "max-w-32" }}
          text={address.postalCode}
          onTextChange={(postalCode) =>
            onAddressChange({ ...address, postalCode })
          }
          errors={errors("postalCode")}
          disabled={disabled}
        />
        <TextInputField
          label={t("Stadt")}
          text={address.city}
          onTextChange={(city) => onAddressChange({ ...address, city })}
          errors={errors("city")}
          disabled={disabled}
        />
      </div>
      <TextInputField
        label={t("Land")}
        text={address.country}
        onTextChange={(country) => onAddressChange({ ...address, country })}
        errors={errors("country")}
        disabled={disabled}
      />
      <TextareaField
        label={t("Kommentar")}
        description={t(
          "Hinweise zur Erreichbarkeit, Gebäudenummern und Ähnliches.",
        )}
        value={address.comment}
        onChange={(e) =>
          onAddressChange({ ...address, comment: e.target.value })
        }
        errors={errors("comment")}
        disabled={disabled}
      />
    </div>
  );
}
